import type { MapStand } from "../shared/types";

export type VenueMapMode = "2d" | "3d";

export const MAP_3D_PITCH = 50;
export const MAP_3D_ROTATION = -18;

const CAMERA_DISTANCE = 2.2;

export interface VenueMapCamera {
  /** Source dimensions keep elevation and label scale independent of the venue. */
  sourceWidth: number;
  sourceHeight: number;
  /** Tallest visible roof in source units; keeps small imported maps in front of the camera. */
  sourceElevation?: number;
  imageWidth: number;
  imageHeight: number;
  stageWidth: number;
  stageHeight: number;
  offsetX?: number;
  offsetY?: number;
  /** Translate the map in the camera plane before the perspective divide. */
  panX?: number;
  panY?: number;
  rotation: number;
  pitch: number;
  /** Keep elevation perspective in 3D even when looking straight down. */
  perspective?: boolean;
}

/** Stylised relief in SVG units, rather than measured building heights. */
export function mapStandHeight(
  stand: string | Pick<MapStand, "category" | "height">,
): number {
  const category = typeof stand === "string" ? stand : stand.category;
  if (typeof stand !== "string" && stand.height !== undefined)
    return stand.height;
  return (
    (
      {
        stage: 12,
        lounge: 9,
        facility: 6,
        exhibitor: 8,
        unassigned: 5,
      } as Record<string, number>
    )[category] || 8
  );
}

type MapPoint = { x: number; y: number; z?: number };

/** Scale the camera with zoom so zooming does not change the perspective. */
export function mapPerspectiveDistance(camera: VenueMapCamera): number {
  return camera.pitch === 0 && camera.perspective !== true
    ? Infinity
    : Math.max(
        1,
        camera.imageWidth,
        camera.imageHeight,
        ((camera.sourceElevation || 0) * camera.imageWidth) /
          camera.sourceWidth,
      ) * CAMERA_DISTANCE;
}

function cameraPoint(camera: VenueMapCamera, point: MapPoint) {
  const yaw = (camera.rotation * Math.PI) / 180;
  const pitch = (camera.pitch * Math.PI) / 180;
  const x = (point.x - 0.5) * camera.imageWidth;
  const y = (point.y - 0.5) * camera.imageHeight;
  const elevation = ((point.z || 0) * camera.imageWidth) / camera.sourceWidth;
  const rotatedY = x * Math.sin(yaw) + y * Math.cos(yaw);
  return {
    x: x * Math.cos(yaw) - y * Math.sin(yaw) + (camera.panX || 0),
    y:
      rotatedY * Math.cos(pitch) -
      elevation * Math.sin(pitch) +
      (camera.panY || 0),
    depth: rotatedY * Math.sin(pitch) + elevation * Math.cos(pitch),
  };
}

/** Perspective camera: normalized floor position plus elevation in SVG units. */
export function projectMapPoint(
  camera: VenueMapCamera,
  point: MapPoint,
): { x: number; y: number } {
  const position = cameraPoint(camera, point);
  const distance = mapPerspectiveDistance(camera);
  const perspective = Number.isFinite(distance)
    ? distance / (distance - position.depth)
    : 1;
  return {
    x: camera.stageWidth / 2 + (camera.offsetX || 0) + position.x * perspective,
    y:
      camera.stageHeight / 2 + (camera.offsetY || 0) + position.y * perspective,
  };
}

/** Reciprocal eye distance is affine on a projected face, unlike world depth. */
export function projectMapDepth(
  camera: VenueMapCamera,
  point: MapPoint,
): number {
  const { depth } = cameraPoint(camera, point);
  const distance = mapPerspectiveDistance(camera);
  return Number.isFinite(distance) ? distance / (distance - depth) : depth;
}

/** Local roof basis for text, measured in source SVG units. */
export function projectMapBasis(camera: VenueMapCamera, point: MapPoint) {
  const position = cameraPoint(camera, point);
  const yaw = (camera.rotation * Math.PI) / 180;
  const pitch = (camera.pitch * Math.PI) / 180;
  const distance = mapPerspectiveDistance(camera);
  const denominator = distance - position.depth;
  const perspective = Number.isFinite(distance) ? distance / denominator : 1;
  const derivative = (
    dx: number,
    dy: number,
    depth: number,
    scale: number,
  ) => ({
    x: perspective * (dx + (position.x * depth) / denominator) * scale,
    y: perspective * (dy + (position.y * depth) / denominator) * scale,
  });
  const x = derivative(
    Math.cos(yaw),
    Math.sin(yaw) * Math.cos(pitch),
    Math.sin(yaw) * Math.sin(pitch),
    camera.imageWidth / camera.sourceWidth,
  );
  const y = derivative(
    -Math.sin(yaw),
    Math.cos(yaw) * Math.cos(pitch),
    Math.cos(yaw) * Math.sin(pitch),
    camera.imageHeight / camera.sourceHeight,
  );
  return { a: x.x, b: x.y, c: y.x, d: y.y };
}

/** Intersect a screen ray with the floor to keep gestures under the fingers. */
export function unprojectMapPoint(
  camera: VenueMapCamera,
  point: { x: number; y: number },
): { x: number; y: number } {
  const yaw = (camera.rotation * Math.PI) / 180;
  const pitch = (camera.pitch * Math.PI) / 180;
  const screenX = point.x - camera.stageWidth / 2 - (camera.offsetX || 0);
  const screenY = point.y - camera.stageHeight / 2 - (camera.offsetY || 0);
  const distance = mapPerspectiveDistance(camera);
  const denominator = distance * Math.cos(pitch) + screenY * Math.sin(pitch);
  // A ray at or above the horizon never hits the floor in front of the eye.
  // This can occur in the empty margin of a small map with several shared pins.
  if (Number.isFinite(distance) && denominator <= distance * 1e-6) {
    return { x: 0.5, y: 0.5 };
  }
  const rotatedY = Number.isFinite(distance)
    ? ((screenY - (camera.panY || 0)) * distance) / denominator
    : screenY - (camera.panY || 0);
  const rotatedX = Number.isFinite(distance)
    ? (screenX * (distance - rotatedY * Math.sin(pitch))) / distance -
      (camera.panX || 0)
    : screenX - (camera.panX || 0);
  return {
    x: camera.imageWidth
      ? (rotatedX * Math.cos(yaw) + rotatedY * Math.sin(yaw)) /
          camera.imageWidth +
        0.5
      : 0.5,
    y: camera.imageHeight
      ? (-rotatedX * Math.sin(yaw) + rotatedY * Math.cos(yaw)) /
          camera.imageHeight +
        0.5
      : 0.5,
  };
}
